import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  FormControl,
  CircularProgress,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  BottomArrowIcon,
  SearchIcon,
  CrossIcon,
  CheckIcon,
  FileIcon,
} from "../Icons";
import {
  IconContainer,
  Label,
  StyledSelect,
  ValueContainer,
  ChipsContainer,
  StyledChip,
  SearchContainer,
  menuPaperStyles,
} from "../Select/Select.styled";
import { Search } from "../Search";
import {
  SectionHeader,
  SectionHeaderText,
  ClearButton,
  ClearButtonText,
  CustomProcedureItem,
  CdtCodeItem,
  CdtCodeLabel,
  CdtCodeText,
  CdtDescriptionText,
  ItemLabel,
  EmptyMessage,
} from "./CDTAndCustomProcedureSelect.styled";
import { colors } from "../../theme/colors";
import { CustomProcedure } from "../../services/organizationService";
import { getOrganizationProcedures } from "../../utils/organizationProceduresCache";

interface InternalCdtCode {
  code: string;
  description: string;
  fee: number;
  durationMinutes: number;
}

export interface CDTAndCustomProcedureValue {
  cdtCodes: string[];
  customProcedureId?: number;
}

interface CDTAndCustomProcedureSelectProps {
  label?: string;
  placeholder?: string;
  value?: CDTAndCustomProcedureValue;
  onChange?: (
    value: CDTAndCustomProcedureValue,
    allCdtCodes: string[],
    allDescriptions: string[],
    totalFee: number,
    totalDurationMinutes: number,
  ) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  name?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export interface CDTAndCustomProcedureSelectHandle {
  open: () => void;
  close: () => void;
  removeCode: (code: string) => void;
  /** Drops the whole Custom Procedure bundle and re-prices the step from what is left. */
  removeCustomProcedure: () => void;
}

const MAX_CDT_CODES = 4;
const INTERNAL_CLINIC_CODES_PREVIEW_COUNT = 3;

function buildAllCodes(
  cdtCodes: string[],
  customProcedure: CustomProcedure | null,
): string[] {
  return [...cdtCodes, ...(customProcedure?.codes ?? [])];
}

function getTotalCodeCount(
  cdtCodes: string[],
  customProcedure: CustomProcedure | null,
): number {
  return cdtCodes.length + (customProcedure?.codes.length ?? 0);
}

function uniqueCodesInOrder(codes: string[]): string[] {
  const order: string[] = [];
  const seen = new Set<string>();

  codes.forEach((code) => {
    if (seen.has(code)) return;
    seen.add(code);
    order.push(code);
  });

  return order;
}

const CDTAndCustomProcedureSelect = forwardRef<
  CDTAndCustomProcedureSelectHandle,
  CDTAndCustomProcedureSelectProps
>(function CDTAndCustomProcedureSelect(
  {
    label,
    placeholder,
    value,
    onChange,
    onFocus,
    onBlur,
    disabled = false,
    fullWidth = false,
    width,
    name,
    id,
    error = false,
    helperText,
    required = false,
    hideTrigger = false,
    open: controlledOpen,
    onOpenChange,
  },
  ref,
) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpenControlled = controlledOpen !== undefined;
  const open = isOpenControlled ? controlledOpen : internalOpen;
  const [searchQuery, setSearchQuery] = useState("");
  const [customProcedures, setCustomProcedures] = useState<CustomProcedure[]>(
    [],
  );
  const [internalClinicCodes, setInternalClinicCodes] = useState<
    CustomProcedure[]
  >([]);
  const [internalCdtCodes, setInternalCdtCodes] = useState<InternalCdtCode[]>(
    [],
  );
  const [proceduresLoading, setProceduresLoading] = useState(false);

  const currentValue = useMemo(
    () => value || { cdtCodes: [], customProcedureId: undefined },
    [value],
  );

  // Loaded up front, not on open: the chips row resolves bundles and recalculates
  // fees through this component without ever opening the dropdown. The fetch is a
  // module-level cache, so mounting many selects still costs one request.
  useEffect(() => {
    let cancelled = false;

    const fetchProcedures = async () => {
      try {
        setProceduresLoading(true);
        const data = await getOrganizationProcedures();
        if (cancelled) return;
        setCustomProcedures(data.customProcedures);
        setInternalClinicCodes(data.internalClinicCodes);
        setInternalCdtCodes(data.cdtCodes);
      } catch (error) {
        console.error("Error fetching procedures:", error);
      } finally {
        if (!cancelled) {
          setProceduresLoading(false);
        }
      }
    };

    void fetchProcedures();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCustomProcedure = useMemo(() => {
    if (!currentValue.customProcedureId) return null;
    return (
      customProcedures.find((p) => p.id === currentValue.customProcedureId) ||
      null
    );
  }, [currentValue.customProcedureId, customProcedures]);

  const isCustomProcedureSelected = Boolean(selectedCustomProcedure);

  const allSelectedCdtCodes = useMemo(
    () => buildAllCodes(currentValue.cdtCodes, selectedCustomProcedure),
    [currentValue.cdtCodes, selectedCustomProcedure],
  );

  const totalCodeCount = useMemo(
    () => getTotalCodeCount(currentValue.cdtCodes, selectedCustomProcedure),
    [currentValue.cdtCodes, selectedCustomProcedure],
  );

  const filterProcedures = useCallback(
    (procedures: CustomProcedure[], limitPreview: boolean) => {
      const withCdtCodes = procedures.filter(
        (proc) => Array.isArray(proc.codes) && proc.codes.length > 0,
      );
      const filtered = !searchQuery.trim()
        ? withCdtCodes
        : withCdtCodes.filter((proc) => {
            const query = searchQuery.toLowerCase();
            const name = proc.name ?? "";
            const description = proc.description ?? "";
            return (
              name.toLowerCase().includes(query) ||
              description.toLowerCase().includes(query) ||
              proc.codes.some((code) =>
                (code ?? "").toLowerCase().includes(query),
              )
            );
          });

      if (limitPreview && !searchQuery.trim()) {
        return filtered.slice(0, INTERNAL_CLINIC_CODES_PREVIEW_COUNT);
      }
      return filtered;
    },
    [searchQuery],
  );

  const filteredCustomProcedures = useMemo(
    () => filterProcedures(customProcedures, false),
    [customProcedures, filterProcedures],
  );

  const filteredInternalClinicCodes = useMemo(
    () => filterProcedures(internalClinicCodes, true),
    [filterProcedures, internalClinicCodes],
  );

  const filteredCdtCodes = useMemo(() => {
    const withCode = internalCdtCodes.filter((c) => c.code);
    if (!searchQuery.trim()) return withCode;
    const query = searchQuery.toLowerCase();
    return withCode.filter(
      (code) =>
        code.code.toLowerCase().includes(query) ||
        (code.description ?? "").toLowerCase().includes(query),
    );
  }, [internalCdtCodes, searchQuery]);

  const setOpenState = useCallback(
    (nextOpen: boolean) => {
      if (!isOpenControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isOpenControlled, onOpenChange],
  );

  const handleOpen = () => {
    setOpenState(true);
  };

  const handleClose = useCallback(() => {
    setOpenState(false);
    setSearchQuery("");
  }, [setOpenState]);

  const getDescriptionsForCodes = useCallback(
    (codes: string[]): string[] => {
      const descMap = new Map<string, string>();
      internalCdtCodes.forEach((c) => descMap.set(c.code, c.description));
      internalClinicCodes.forEach((proc) => {
        proc.codes.forEach((code) => {
          if (!descMap.has(code)) {
            descMap.set(code, proc.description || proc.name);
          }
        });
      });
      return codes.map((code) => descMap.get(code) ?? "");
    },
    [internalCdtCodes, internalClinicCodes],
  );

  /**
   * Calculate the total fee and duration for the given selection.
   * - Custom procedure contributes its own fee/duration.
   * - Fully selected internal clinic codes contribute their procedure fee/duration.
   * - Extra individually-selected CDT codes (not in a bundle) add their own.
   */
  const calculateTotals = useCallback(
    (
      extraCdtCodes: string[],
      customProc: CustomProcedure | null,
    ): { totalFee: number; totalDuration: number } => {
      let totalFee = customProc?.fee ?? 0;
      let totalDuration = customProc?.durationMinutes ?? 0;
      const bundleCodes = new Set(customProc?.codes ?? []);
      const coveredByInternal = new Set<string>();

      internalClinicCodes.forEach((proc) => {
        const isFullySelected =
          proc.codes.length > 0 &&
          proc.codes.every(
            (code) =>
              extraCdtCodes.includes(code) && !bundleCodes.has(code),
          );
        if (!isFullySelected) return;

        totalFee += proc.fee ?? 0;
        totalDuration += proc.durationMinutes ?? 0;
        proc.codes.forEach((code) => coveredByInternal.add(code));
      });

      extraCdtCodes
        .filter(
          (code) => !bundleCodes.has(code) && !coveredByInternal.has(code),
        )
        .forEach((code) => {
          const found = internalCdtCodes.find((c) => c.code === code);
          totalFee += found?.fee ?? 0;
          totalDuration += found?.durationMinutes ?? 0;
        });
      return { totalFee, totalDuration };
    },
    [internalCdtCodes, internalClinicCodes],
  );

  const emitChange = useCallback(
    (
      nextCdtCodes: string[],
      customProcedure: CustomProcedure | null,
      nextCustomProcedureId?: number,
    ) => {
      const normalizedCdtCodes = uniqueCodesInOrder(nextCdtCodes);
      const allCodes = buildAllCodes(normalizedCdtCodes, customProcedure);
      const { totalFee, totalDuration } = calculateTotals(
        normalizedCdtCodes,
        customProcedure,
      );

      onChange?.(
        {
          cdtCodes: normalizedCdtCodes,
          customProcedureId:
            nextCustomProcedureId ?? customProcedure?.id ?? undefined,
        },
        allCodes,
        getDescriptionsForCodes(allCodes),
        totalFee,
        totalDuration,
      );
    },
    [onChange, getDescriptionsForCodes, calculateTotals],
  );

  const removeCode = useCallback(
    (codeToRemove: string) => {
      const newCdtCodes = currentValue.cdtCodes.filter(
        (code) => code !== codeToRemove,
      );
      emitChange(newCdtCodes, selectedCustomProcedure);
    },
    [currentValue.cdtCodes, emitChange, selectedCustomProcedure],
  );

  const handleRemoveChip = useCallback(
    (codeToRemove: string, event: React.MouseEvent) => {
      event.stopPropagation();
      removeCode(codeToRemove);
    },
    [removeCode],
  );

  const handleRemoveCustomProcedure = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      emitChange(currentValue.cdtCodes, null, undefined);
    },
    [currentValue.cdtCodes, emitChange],
  );

  useImperativeHandle(
    ref,
    () => ({
      open: () => setOpenState(true),
      close: () => handleClose(),
      removeCode: (codeToRemove: string) => {
        handleRemoveChip(codeToRemove, {
          stopPropagation: () => undefined,
        } as React.MouseEvent);
      },
      removeCustomProcedure: () => {
        handleRemoveCustomProcedure({
          stopPropagation: () => undefined,
        } as React.MouseEvent);
      },
    }),
    [
      handleRemoveChip,
      handleRemoveCustomProcedure,
      handleClose,
      setOpenState,
    ],
  );

  const handleCustomProcedureSelect = useCallback(
    (procedure: CustomProcedure) => {
      const procedureCodes = new Set(procedure.codes);
      const additionalCodes = currentValue.cdtCodes.filter(
        (code) => !procedureCodes.has(code),
      );

      if (procedure.codes.length + additionalCodes.length > MAX_CDT_CODES) {
        return;
      }

      emitChange(additionalCodes, procedure, procedure.id);
    },
    [currentValue.cdtCodes, emitChange],
  );

  const isInternalProcedureSelected = useCallback(
    (procedure: CustomProcedure) =>
      procedure.codes.length > 0 &&
      procedure.codes.every((code) => currentValue.cdtCodes.includes(code)),
    [currentValue.cdtCodes],
  );

  const canSelectInternalProcedure = useCallback(
    (procedure: CustomProcedure) => {
      if (isInternalProcedureSelected(procedure)) {
        return true;
      }

      const bundleCodes = new Set(selectedCustomProcedure?.codes ?? []);
      const codesToAdd = procedure.codes.filter(
        (code) =>
          !currentValue.cdtCodes.includes(code) && !bundleCodes.has(code),
      );

      return totalCodeCount + codesToAdd.length <= MAX_CDT_CODES;
    },
    [
      currentValue.cdtCodes,
      isInternalProcedureSelected,
      selectedCustomProcedure,
      totalCodeCount,
    ],
  );

  const handleInternalProcedureSelect = useCallback(
    (procedure: CustomProcedure) => {
      const procedureCodes = new Set(procedure.codes);

      if (isInternalProcedureSelected(procedure)) {
        const nextCdtCodes = currentValue.cdtCodes.filter(
          (code) => !procedureCodes.has(code),
        );
        emitChange(nextCdtCodes, selectedCustomProcedure);
        return;
      }

      const bundleCodes = new Set(selectedCustomProcedure?.codes ?? []);
      const codesToAdd = procedure.codes.filter(
        (code) =>
          !currentValue.cdtCodes.includes(code) && !bundleCodes.has(code),
      );

      if (totalCodeCount + codesToAdd.length > MAX_CDT_CODES) {
        return;
      }

      emitChange(
        [...currentValue.cdtCodes, ...codesToAdd],
        selectedCustomProcedure,
      );
    },
    [
      currentValue.cdtCodes,
      emitChange,
      isInternalProcedureSelected,
      selectedCustomProcedure,
      totalCodeCount,
    ],
  );

  const handleCdtCodeToggle = useCallback(
    (code: string) => {
      if (selectedCustomProcedure?.codes.includes(code)) {
        return;
      }
      if (currentValue.cdtCodes.includes(code)) {
        removeCode(code);
        return;
      }
      if (totalCodeCount >= MAX_CDT_CODES) {
        return;
      }

      emitChange([...currentValue.cdtCodes, code], selectedCustomProcedure);
    },
    [
      currentValue.cdtCodes,
      emitChange,
      removeCode,
      selectedCustomProcedure,
      totalCodeCount,
    ],
  );

  const canSelectCustomProcedure = useCallback(
    (procedure: CustomProcedure) => {
      if (
        selectedCustomProcedure &&
        selectedCustomProcedure.id !== procedure.id
      ) {
        return false;
      }
      const procedureCodes = new Set(procedure.codes);
      const additionalCodes = currentValue.cdtCodes.filter(
        (code) => !procedureCodes.has(code),
      );
      return procedure.codes.length + additionalCodes.length <= MAX_CDT_CODES;
    },
    [currentValue.cdtCodes, selectedCustomProcedure],
  );

  const getDisplayValue = () => {
    if (allSelectedCdtCodes.length === 0) return placeholder || "";

    if (selectedCustomProcedure) {
      const bundleLabel = `${selectedCustomProcedure.codes.join(", ")} - ${selectedCustomProcedure.name}`;
      return bundleLabel;
    }

    return uniqueCodesInOrder(currentValue.cdtCodes).join(", ");
  };

  const renderChips = () => {
    if (allSelectedCdtCodes.length === 0) return null;

    return (
      <ChipsContainer sx={{ minWidth: 0 }}>
        {uniqueCodesInOrder(currentValue.cdtCodes).map((code) => (
          <StyledChip
            key={code}
            label={code}
            size="small"
            onDelete={(e) => handleRemoveChip(code, e)}
            deleteIcon={
              <Box component="span" sx={{ cursor: "pointer", display: "flex" }}>
                <CrossIcon width="12" height="12" />
              </Box>
            }
            onMouseDown={(e) => e.stopPropagation()}
          />
        ))}
        {selectedCustomProcedure && (
          <Tooltip
            title={`${selectedCustomProcedure.codes.join(", ")} - ${selectedCustomProcedure.name}`}
            placement="top"
            arrow
            enterDelay={300}
          >
            <StyledChip
              label={`${selectedCustomProcedure.codes.join(", ")} - ${selectedCustomProcedure.name}`}
              size="small"
              onDelete={handleRemoveCustomProcedure}
              deleteIcon={
                <Box
                  component="span"
                  sx={{ cursor: "pointer", display: "flex" }}
                >
                  <CrossIcon width="12" height="12" />
                </Box>
              }
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: colors.background.lightBlue,
                border: `1px solid ${colors.primary.main}`,
                maxWidth: "220px",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                },
              }}
            />
          </Tooltip>
        )}
      </ChipsContainer>
    );
  };

  const renderProcedureItems = (
    procedures: CustomProcedure[],
    emptyMessageKey: string,
    emptyMessageDefault: string,
    keyPrefix: string,
    mode: "custom" | "internal",
  ) => {
    if (procedures.length === 0) {
      return (
        <MenuItem disabled>
          <EmptyMessage>
            {t(emptyMessageKey, emptyMessageDefault)}
          </EmptyMessage>
        </MenuItem>
      );
    }

    return procedures.map((procedure) => {
      const isSelected =
        mode === "internal"
          ? isInternalProcedureSelected(procedure)
          : selectedCustomProcedure?.id === procedure.id;
      const canSelect =
        mode === "internal"
          ? canSelectInternalProcedure(procedure)
          : canSelectCustomProcedure(procedure);

      return (
        <CustomProcedureItem
          key={`${keyPrefix}-${procedure.id}`}
          isSelected={isSelected}
          canSelect={canSelect || isSelected}
          onClick={(e) => {
            e.stopPropagation();
            if (mode === "internal") {
              if (canSelect || isSelected) {
                handleInternalProcedureSelect(procedure);
              }
              return;
            }

            if (isSelected) {
              handleRemoveCustomProcedure(e);
            } else if (canSelect) {
              handleCustomProcedureSelect(procedure);
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              gap: 1,
              minWidth: 0,
            }}
          >
            <FileIcon width="16" height="16" fill={colors.text.secondary} />
            {mode === "internal" ? (
              <CdtCodeLabel>
                <CdtCodeText>{procedure.codes.join(", ")}</CdtCodeText>
                {(procedure.description || procedure.name) && (
                  <>
                    <CdtCodeText> - </CdtCodeText>
                    <CdtDescriptionText>
                      {procedure.description || procedure.name}
                    </CdtDescriptionText>
                  </>
                )}
              </CdtCodeLabel>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "4px",
                }}
              >
                <ItemLabel>{procedure.name}</ItemLabel>
                {procedure.codes.length > 0 && (
                  <>
                    <ItemLabel sx={{ color: colors.text.secondary }}>
                      {" -"}
                    </ItemLabel>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: colors.text.secondary,
                      }}
                    >
                      {procedure.codes.join(", ")}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
          {isSelected && (
            <Box
              sx={{
                ml: 2,
                display: "flex",
                width: "16px",
                flexShrink: 0,
              }}
            >
              <CheckIcon
                fill={colors.status.activeText}
                width="16"
                height="16"
              />
            </Box>
          )}
        </CustomProcedureItem>
      );
    });
  };

  const renderCodeItems = (codes: InternalCdtCode[]) => {
    if (codes.length === 0) {
      return (
        <MenuItem disabled>
          <EmptyMessage>
            {t("editableStep.noCdtCodesFound", "No CDT codes found")}
          </EmptyMessage>
        </MenuItem>
      );
    }

    return codes.map((code) => {
      const isFromBundle = Boolean(
        selectedCustomProcedure?.codes.includes(code.code),
      );
      const isSelected =
        currentValue.cdtCodes.includes(code.code) || isFromBundle;
      const canAdd =
        !isFromBundle &&
        !currentValue.cdtCodes.includes(code.code) &&
        totalCodeCount < MAX_CDT_CODES;
      const canInteract = !isFromBundle && (isSelected || canAdd);

      return (
        <CdtCodeItem
          key={`cdt-${code.code}`}
          isSelected={isSelected}
          isFromBundle={isFromBundle}
          isSelectable={canInteract}
          onClick={(e) => {
            e.stopPropagation();
            if (isFromBundle) return;
            handleCdtCodeToggle(code.code);
          }}
        >
          <CdtCodeLabel>
            <CdtCodeText>{code.code}</CdtCodeText>
            <CdtCodeText> - </CdtCodeText>
            <CdtDescriptionText>{code.description}</CdtDescriptionText>
          </CdtCodeLabel>
          {isSelected && (
            <Box
              sx={{
                ml: 2,
                display: "flex",
                width: "16px",
                flexShrink: 0,
              }}
            >
              <CheckIcon
                fill={colors.status.activeText}
                width="16"
                height="16"
              />
            </Box>
          )}
        </CdtCodeItem>
      );
    });
  };

  return (
    <Box
      sx={{
        width: hideTrigger ? "100%" : fullWidth ? "100%" : width,
        height: hideTrigger ? "100%" : undefined,
        position: hideTrigger ? "absolute" : "relative",
        inset: hideTrigger ? 0 : undefined,
        opacity: hideTrigger ? 0 : 1,
        overflow: hideTrigger ? "hidden" : "visible",
        pointerEvents: hideTrigger ? "none" : "auto",
      }}
    >
      {label && !hideTrigger && (
        <Label>
          {label}
          {required && (
            <Box component="span" sx={{ color: "inherit" }}>
              {" "}
              *
            </Box>
          )}
        </Label>
      )}
      <FormControl fullWidth sx={{ height: "100%", width: "100%" }}>
        <StyledSelect
          value={allSelectedCdtCodes}
          onChange={() => {}}
          disabled={disabled}
          multiple
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            "& .MuiSelect-select": {
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              minWidth: 0,
            },
          }}
          displayEmpty
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          IconComponent={() => (
            <IconContainer>
              <BottomArrowIcon width="16px" height="16px" />
            </IconContainer>
          )}
          renderValue={() => (
            <ValueContainer sx={{ minWidth: 0, overflow: "hidden" }}>
              <Box sx={{ mr: 1, flexShrink: 0, display: "flex" }}>
                <SearchIcon width="16px" height="16px" />
              </Box>
              {renderChips() || (
                <Typography
                  sx={{
                    color:
                      allSelectedCdtCodes.length > 0
                        ? `${colors.text.primary}!important`
                        : `${colors.text.secondary}!important`,
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getDisplayValue()}
                </Typography>
              )}
            </ValueContainer>
          )}
          name={name}
          id={id}
          onFocus={onFocus}
          onBlur={onBlur}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              sx: {
                ...menuPaperStyles,
                maxHeight: 350,
                minWidth: "800px!important",
                padding: "0 10px 10px 10px",
              },
            },
          }}
        >
          <SearchContainer>
            <Search
              placeholder={t(
                "editableStep.searchProceduresOrCodes",
                "Search procedures or codes...",
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
            />
          </SearchContainer>

          {proceduresLoading ? (
            <MenuItem disabled>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  py: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            </MenuItem>
          ) : (
            <>
              <SectionHeader>
                <SectionHeaderText>
                  {t("editableStep.customProcedures", "Custom Procedures")}
                </SectionHeaderText>
              </SectionHeader>

              {isCustomProcedureSelected && (
                <ClearButton
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleRemoveCustomProcedure(e);
                  }}
                >
                  <ClearButtonText>
                    {t(
                      "editableStep.clearCustomProcedure",
                      "Clear custom procedure to select CDT codes",
                    )}
                  </ClearButtonText>
                </ClearButton>
              )}

              {renderProcedureItems(
                filteredCustomProcedures,
                "editableStep.noCustomProceduresFound",
                "No custom procedures found",
                "custom",
                "custom",
              )}

              <SectionHeader
                sx={{
                  borderTop: `1px solid ${colors.border.light}`,
                  marginTop: "8px",
                }}
              >
                <SectionHeaderText>
                  {t(
                    "editableStep.internalClinicCodes",
                    "Internal Clinic Codes",
                  )}
                </SectionHeaderText>
              </SectionHeader>

              {renderProcedureItems(
                filteredInternalClinicCodes,
                "editableStep.noInternalClinicCodesFound",
                "No internal clinic codes found",
                "internal",
                "internal",
              )}

              <SectionHeader
                sx={{
                  borderTop: `1px solid ${colors.border.light}`,
                  marginTop: "8px",
                }}
              >
                <SectionHeaderText>
                  {t("editableStep.cdtCodes", "CDT Codes")}
                </SectionHeaderText>
              </SectionHeader>

              {isCustomProcedureSelected && (
                <ClearButton
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleRemoveCustomProcedure(e);
                  }}
                >
                  <ClearButtonText>
                    {t(
                      "editableStep.clearCustomProcedure",
                      "Clear custom procedure to select CDT codes",
                    )}
                  </ClearButtonText>
                </ClearButton>
              )}

              {renderCodeItems(filteredCdtCodes)}
            </>
          )}
        </StyledSelect>
      </FormControl>

      {helperText && !hideTrigger && (
        <Typography
          variant="caption"
          sx={{
            color: error ? "status.error" : "text.secondary",
            fontSize: "12px",
            mt: 0.5,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

export default CDTAndCustomProcedureSelect;
